<template>
  <div class="budget-range-slider">
    <mf-info-card
      v-if="offerWithSegment"
      icon="info"
      variant="info"
      description="A oferta selecionada continha um segmento vinculado a ela, por isso não é possível alterar o público-alvo nesta etapa."
      class="my-6"
      max-width="780px"
    />

    <h3 class="mb-4 mt-6">Distribuição dos consumidores</h3>

    <div :class="{ 'disabled-opacity': disableField }">
      <v-col v-if="hundredPercent && hasHundredPercentError" class="pa-0 pl-2">
        <mf-icon small color="vermelho-l20"> info </mf-icon>
        <span class="hundred-percent-error pl-2 error--text">A soma das escolhas de clientes deve ser 100%.</span>
      </v-col>
      <vue-slider
        v-model="value"
        :disabled="disableField"
        :class="{ 'disabled-opacity': disableField }"
        :process="processConfigs"
        dot-size="18"
        height="9px"
        :min="0"
        :max="100"
        :interval="1"
        tooltip="none"
      />
      <div class="legend-container-out d-flex justify-space-between mt-3">
        <budget-range-slider-legend-input color="#0FC94D" :disabled="disableField" :value.sync="actives" legend="Ativos" @input="recalculate" />
        <budget-range-slider-legend-input
          color="#F3AF00"
          :disabled="disableField"
          :value.sync="recentInactives"
          legend="Inativos recentes"
          @input="recalculate"
        />
        <budget-range-slider-legend-input color="#F36600" :disabled="disableField" :value.sync="atRisk" legend="Risco de perder" @input="recalculate" />
        <budget-range-slider-legend-input color="#B3280D" :disabled="disableField" :value.sync="losses" legend="Perdidos" @input="recalculate" />
        <budget-range-slider-legend-input color="#6B7580" :disabled="disableField" :value.sync="neverBought" legend="Nunca compraram" @input="recalculate" />
      </div>
    </div>
  </div>
</template>

<script>
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/antd.css'
import { mapGetters } from 'vuex'

import { mariaCampaign } from '@/modules/maria/campanha-da-maria-v2/mixins/maria-campaign'
import BudgetRangeSliderLegendInput from '@/modules/maria/campanha-da-maria/views/components/BudgetRangeSliderLegendInput.vue'
import { QUERY_AGGREGATED_CLIENTS_STATUS_INFO } from '@/modules/maria/campanha-da-maria/graphql'

export default {
  name: 'BudgetRangeSlider',
  components: {
    BudgetRangeSliderLegendInput,
    VueSlider,
  },
  mixins: [mariaCampaign],
  props: {
    hundredPercentError: {
      type: Boolean,
      default: false,
    },
    hasHundredPercentError: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    value: [60, 75, 85, 95],
    actives: 20,
    recentInactives: 20,
    atRisk: 20,
    losses: 20,
    neverBought: 20,
    processConfigs: (dotsPos) => [
      [0, dotsPos[0], { backgroundColor: '#0FC94D' }],
      [dotsPos[0], dotsPos[1], { backgroundColor: '#F3AF00' }],
      [dotsPos[1], dotsPos[2], { backgroundColor: '#F36600' }],
      [dotsPos[2], dotsPos[3], { backgroundColor: '#B3280D' }],
      [dotsPos[3], 100, { backgroundColor: '#C3C8CD' }],
    ],
  }),
  computed: {
    ...mapGetters(['mariaCampaign']),
    disableField() {
      return (this.selectedSegments && this.selectedSegments.length > 0) || this.isMariaCampaignActive || this.isMariaCampaignFinished
    },
    hundredPercent: {
      get() {
        return this.hundredPercentError
      },
      set(value) {
        this.$emit('update:hundred-percent-error', value)
      },
    },
  },
  watch: {
    selectedSegments(newValue) {
      this.recalculateClients(newValue)
    },
    value() {
      this.syncLegend()
      this.updateClients()
      this.hundredPercent = false
      this.$emit('update:has-hundred-percent-error', false)
    },
  },
  mounted() {
    if (this.campaign.segments && this.campaign.segments.length) {
      this.recalculateClients(this.campaign.segments)
    }

    const clientsDistribution = this.campaign?.client_targets_distribution

    if (clientsDistribution) {
      const sum =
        clientsDistribution.actives +
        clientsDistribution.recent_inactives +
        clientsDistribution.at_risk +
        clientsDistribution.losses +
        clientsDistribution.never_bought

      const multiplier = sum === 100 ? 1 : 100

      clientsDistribution.actives = typeof clientsDistribution.actives === 'number' ? clientsDistribution.actives * multiplier : 60
      clientsDistribution.recent_inactives = typeof clientsDistribution.recent_inactives === 'number' ? clientsDistribution.recent_inactives * multiplier : 15
      clientsDistribution.at_risk = typeof clientsDistribution.at_risk === 'number' ? clientsDistribution.at_risk * multiplier : 10
      clientsDistribution.losses = typeof clientsDistribution.losses === 'number' ? clientsDistribution.losses * multiplier : 10
      clientsDistribution.never_bought = typeof clientsDistribution.never_bought === 'number' ? clientsDistribution.never_bought * multiplier : 5

      this.value = [
        clientsDistribution.actives,
        clientsDistribution.actives + clientsDistribution.recent_inactives,
        clientsDistribution.actives + clientsDistribution.recent_inactives + clientsDistribution.at_risk,
        100 - clientsDistribution.never_bought,
      ]
    }

    this.syncLegend()
  },
  methods: {
    roundValue(value) {
      return Math.round(value * 100) / 100
    },
    recalculate(metrics) {
      if (metrics) {
        const { client_active_count, client_at_risk_count, client_losses_count, client_never_bought_count, client_recent_inactive_count, total_count } = metrics
        const formatValue = (value) => Math.round((value * 10000) / total_count) / 100

        this.actives = formatValue(client_active_count)
        this.recentInactives = formatValue(client_recent_inactive_count)
        this.atRisk = formatValue(client_at_risk_count)
        this.losses = formatValue(client_losses_count)
        this.neverBought = formatValue(client_never_bought_count)

        const residue = this.actives + this.recentInactives + this.atRisk + this.losses + this.neverBought - 100
        if (residue !== 0) {
          if (this.actives) {
            this.actives -= residue
          } else if (this.recentInactives) {
            this.recentInactives -= residue
          } else if (this.atRisk) {
            this.atRisk -= residue
          } else if (this.losses) {
            this.losses -= residue
          } else if (this.neverBought) {
            this.neverBought -= residue
          }
        }

        this.value = [this.actives, this.actives + this.recentInactives, this.actives + this.recentInactives + this.atRisk, 100 - this.neverBought]
        this.updateClients()
      } else {
        const activesRounded = this.roundValue(this.actives)
        const recentInactivesRounded = this.roundValue(this.recentInactives)
        const atRiskRounded = this.roundValue(this.atRisk)
        const lossesRounded = this.roundValue(this.losses)
        const neverBoughtRounded = this.roundValue(this.neverBought)

        const total = activesRounded + recentInactivesRounded + atRiskRounded + lossesRounded + neverBoughtRounded

        if (Math.abs(total - 100) < 0.1) {
          this.value = [this.actives, this.actives + this.recentInactives, this.actives + this.recentInactives + this.atRisk, 100 - this.neverBought]
          this.hundredPercent = false
          this.$emit('update:has-hundred-percent-error', false)
          this.updateClients()
        } else this.hundredPercent = true
      }
    },
    syncLegend() {
      this.actives = this.value[0]
      this.recentInactives = this.value[1] - this.value[0]
      this.atRisk = this.value[2] - this.value[1]
      this.losses = this.value[3] - this.value[2]
      this.neverBought = 100 - this.value[3]
    },
    updateClients() {
      const clients = {
        actives: this.actives,
        recent_inactives: this.recentInactives,
        at_risk: this.atRisk,
        losses: this.losses,
        never_bought: this.neverBought,
      }
      this.setOmniCampaignKey({ key: 'client_targets_distribution', payload: clients })
    },
    async recalculateClients(segments) {
      this.hundredPercent = false
      this.$emit('update:has-hundred-percent-error', false)
      if (!segments.length) {
        this.value = [60, 75, 85, 95]
        return
      }
      const segments_ids = segments.map((segment) => segment._id)
      try {
        this.$emit('change-loading', true)
        const { data } = await this.$apollo.query({
          query: QUERY_AGGREGATED_CLIENTS_STATUS_INFO,
          variables: { segments_ids },
          fetchPolicy: 'no-cache',
        })
        const metrics = data.getAggregatedClientStatusInfo.all
        if (metrics.total_count === 0) this.value = [60, 75, 85, 95]
        else this.recalculate(metrics)
      } catch (error) {
        const error_segments = segments
          .map((segment) => {
            if (segment.clients_qty == null) return segment.name
          })
          .filter((segment) => segment)

        const error_names = error_segments
          .map((segment, index) => {
            if (error_segments.length === 3 && index === 0) return segment + ', '
            else if (error_segments.length === 1) return segment
            else if (index === error_segments.length - 2) return segment + ' e '
            else return segment + ' '
          })
          .join('')

        const message =
          error_segments.length > 1
            ? `Os segmentos ${error_names}possuem erros. Tente utilizar outro grupo de clientes para continuar com a sua campanha.`
            : `O segmento ${error_names} possui erro. Tente utilizar outro grupo de clientes para continuar com a sua campanha.`
        this.$snackbar({ message, snackbarColor: 'error' })
        this.value = [60, 75, 85, 95]
      } finally {
        this.$emit('change-loading', false)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.hundred-percent-error {
  font-size: 12px;
  letter-spacing: 0.2px;
  color: $vermelho-0;
}

::v-deep .vue-slider-dot-handle {
  border-color: white !important;
  box-shadow: 0px 2px 4px rgb(0 0 0 / 16%) !important;
}

::v-deep .vue-slider-dot {
  z-index: 3;
}

.budget-range-slider {
  max-width: 900px;
  h3 {
    color: $cinza-d10;
    font-weight: 700;
    font-size: 14px;
  }
  & .disabled-opacity {
    opacity: 0.7;
  }

  & .legend-container-out {
    margin-left: 8px;
    gap: 4px;
    width: 100%;
  }

  & .icon-container {
    align-items: center !important;
    gap: 10px;
    & p {
      margin: 0;
      max-width: 330px;
      color: $cinza-0;
      font-size: 14px;
      line-height: 120%;
    }
  }
}

@media screen and (max-width: 1200px) {
  .budget-range-slider {
    & .legend-container-out {
      display: grid !important;
      grid-template-columns: 1fr;
      gap: 20px;

      & .legend-container {
        justify-content: flex-start !important;
      }
    }
  }
}
</style>
